import { create } from 'zustand';
//import { persist, createJSONStorage } from 'zustand/middleware';
//import { jwtDecode } from 'jwt-decode';
import { User, UserCreate, UserUpdate } from '../types/user';
import api from '../config/axios';
import { useStatusStore } from './statusStore';
import { STATUS_NAMES, STATUS_TYPES } from '../constants/statusConstants';
import { AUTH, USERS } from '../config/apiPaths';

/**
 * Token Storage System
 * 
 * This provides a flexible token storage mechanism that supports multiple backends.
 * To change the storage method, simply change the value of USE_COOKIE_STORAGE below.
 */

// CONFIGURATION - Set to true to use cookies, false to use localStorage
const USE_COOKIE_STORAGE = true;

// Token storage strategy interface
interface TokenStorage {
  getToken: () => string | null;
  setToken: (token: string | null) => void;
  clearToken: () => void;
}

// Cookie-based token storage implementation
const cookieStorage: TokenStorage = {
  getToken: () => {
    const cookies = document.cookie.split(';');
    const tokenCookie = cookies.find(cookie => cookie.trim().startsWith('auth_token='));
    if (tokenCookie) {
      // Properly extract the token value and clean it
      const tokenValue = tokenCookie.split('=')[1];
      if (tokenValue) {
        // Clean the token of any potential whitespace
        const cleanToken = tokenValue.trim();
        return cleanToken;
      }
    }
    console.log('No auth_token cookie found');
    return null;
  },
  setToken: (token) => {
    if (token) {
      // Set secure cookie with same-site attribute and no whitespace in the value
      const secure = window.location.protocol === 'https:' ? 'Secure;' : '';
      
      // Get the current domain
      const domain = window.location.hostname;
      
      // Ensure token doesn't have any whitespace or encoding issues
      const cleanToken = token.trim();
      
      // For localhost, don't specify domain as it can cause issues
      const isLocalhost = domain === 'localhost' || domain === '127.0.0.1';
      if (isLocalhost) {
        document.cookie = `auth_token=${cleanToken}; Path=/; ${secure} SameSite=Lax; max-age=2592000`;
      } else {
        document.cookie = `auth_token=${cleanToken}; Path=/; Domain=${domain}; ${secure} SameSite=Lax; max-age=2592000`;
      }
    } else {
      // When clearing, don't use domain for localhost
      const domain = window.location.hostname;
      const isLocalhost = domain === 'localhost' || domain === '127.0.0.1';
      if (isLocalhost) {
        document.cookie = `auth_token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
      } else {
        document.cookie = `auth_token=; Path=/; Domain=${domain}; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
      }
    }
  },
  clearToken: () => {
    // Use the same domain parameter when clearing token
    const domain = window.location.hostname;
    document.cookie = `auth_token=; Path=/; Domain=${domain}; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
  }
};

// LocalStorage-based token storage implementation
const localStorageStorage: TokenStorage = {
  getToken: () => localStorage.getItem('token'),
  setToken: (token) => {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  },
  clearToken: () => localStorage.removeItem('token')
};

// Select which storage method to use based on configuration
export const tokenStorage: TokenStorage = USE_COOKIE_STORAGE ? cookieStorage : localStorageStorage;

// Extend the Window interface to include our custom property
declare global {
  interface Window {
    tokenRefreshTimeout?: number; // Changed from NodeJS.Timeout to number for browser setTimeout
  }
}

// Helper function to convert file to base64 string
const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
};

interface UserStore {
  // Data
  users: User[];
  currentUser: User | null;
  isLoading: boolean;
  error: string | null;
  token: string | null;
  passwordResetSuccess: boolean;

  // Data Fetching
  fetchUsers: () => Promise<User[]>;
  fetchCurrentUser: () => Promise<User | null>;
  
  // User Management
  createUser: (userData: UserCreate) => Promise<User>;
  updateUser: (id: number, updates: UserUpdate, profilePicture?: File | string | null) => Promise<User>;
  deleteUser: (id: number) => Promise<void>;
  
  // Login Management
  login: (email: string, password: string) => Promise<User | null>;
  loginDemo: () => Promise<User | null>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  checkAuth: (forceRefresh?: boolean) => Promise<boolean>;
  
  // Password Reset
  requestPasswordReset: (email: string) => Promise<boolean>;
  resetPassword: (token: string, password: string) => Promise<boolean>;
  setPasswordResetSuccess: (success: boolean) => void;
  
  // Store Utilities
  setToken: (token: string | null) => void;
  setCurrentUser: (user: User | null) => void;
  clearError: () => void;
  
  // Lookup Methods
  getUserById: (
    id: number, 
    options?: { 
      forceRefresh?: boolean;
      includeContact?: boolean;
    }
  ) => Promise<User | undefined>;
  
  getUserByEmail: (
    email: string, 
    options?: { 
      includeContact?: boolean;
    }
  ) => Promise<User | undefined>;
}

export const useUserStore = create<UserStore>((set, get) => ({
  users: [],
  currentUser: null,
  token: tokenStorage.getToken(),
  isLoading: false,
  error: null,
  passwordResetSuccess: false,

  fetchUsers: async () => {
    console.log('[UserStore] Fetching users...');
    set({ isLoading: true, error: null });
    
    try {
      console.log('[UserStore] Fetching users from:', USERS.ALL);
      const response = await api.get(USERS.ALL);
      console.log('[UserStore] Users API response status:', response.status);
      
      // Handle the response
      const users = Array.isArray(response.data) ? response.data : [response.data];
      console.log(`[UserStore] Processed ${users.length} users`);
      
      // Users should already have the correct structure from the backend
      // Just ensure required fields are present
      const validUsers = users.map(user => ({
        ...user,
        role: user.role || 'user'
      }));
      
      set({ users: validUsers, isLoading: false });
      console.log('[UserStore] Users fetched and stored successfully');
      
      return validUsers;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch users';
      console.error('Error in fetchUsers:', {
        error,
        message: errorMessage,
        response: (error as any)?.response?.data
      });
      
      set({ users: [], error: errorMessage, isLoading: false });
      throw error; // Re-throw to allow component to handle the error
    }
  },

  fetchCurrentUser: async (): Promise<User | null> => {
    const token = get().token || tokenStorage.getToken();
    if (!token) {
      console.warn('[UserStore] No token available for fetchCurrentUser');
      return null;
    }

    set({ isLoading: true, error: null });
    
    try {
      const response = await api.get(USERS.ME);
      const userData = response.data;

      // Get contact data from either Contact (capital C) or contact field
      const contactData = userData.Contact || userData.contact || {};
      
      // The backend sends profile pictures as base64 data URLs, no need to modify them
      
      // Format user data consistently
      const user: User = {
        id: userData.id || userData.ID,
        username: userData.username || userData.Username || userData.email || '',
        contact_id: userData.contact_id || userData.ContactID || contactData.id,
        contact: contactData,
        role: (userData.role || userData.Role || 'user') as 'user' | 'admin' | 'manager',
        organization_id: userData.organization_id || userData.OrganizationID,
        status_id: userData.status_id || userData.StatusID,
        last_login: userData.last_login || userData.LastLogin || null,
        created_at: userData.created_at || userData.CreatedAt,
        updated_at: userData.updated_at || userData.UpdatedAt
      };
      
      // Update the store with fresh user data
      set({ 
        currentUser: user,
        isLoading: false,
        error: null
      });
      
      console.log('[UserStore] Current user profile picture:', {
        hasProfilePicture: !!user.contact?.profile_picture,
        profilePictureType: typeof user.contact?.profile_picture,
        profilePicturePreview: user.contact?.profile_picture?.substring?.(0, 100) + '...'
      });
      
      console.log('[UserStore] Current user fetched successfully:', user.username);
      return user;
    } catch (error: any) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch current user';
      console.error('[UserStore] Error in fetchCurrentUser:', {
        error,
        message: errorMessage,
        response: error?.response?.data
      });
      
      set({ error: errorMessage, isLoading: false });
      
      // If it's a 401, the user might need to re-authenticate
      if (error.response?.status === 401) {
        set({ currentUser: null, token: null });
        tokenStorage.clearToken();
      }
      
      return null;
    }
  },

  createUser: async (userData: UserCreate) => {
    set({ isLoading: true, error: null });
    try {
      // Get the active status ID dynamically
      const statusStore = useStatusStore.getState();
      const activeStatusId = statusStore.getStatusIdByNameAndType(STATUS_NAMES.ACTIVE, STATUS_TYPES.USER);

      // The backend expects the user data with contact information
      // The Go backend will handle creating both User and Contact entities
      const userToCreate: UserCreate = {
        username: userData.username,
        role: userData.role,
        status_id: userData.status_id ?? activeStatusId ?? 79, // Fallback to 79 as last resort
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        email: userData.email || '',
        phone: userData.phone || '',
        profile_picture: userData.profile_picture || null
      };
      
      const response = await api.post(USERS.ALL, userToCreate);
      const createdUser = response.data as User;
      
      set(state => ({
        users: [...state.users, createdUser],
        isLoading: false
      }));
      
      return createdUser;
    } catch (error) {
      set({ error: 'Failed to create user', isLoading: false });
      console.error('Error creating user:', error);
      throw error;
    }
  },


  
  updateUser: async (id: number, updates: UserUpdate, profilePicture?: File | string | null) => {
    try {
      console.log('Updating user with profile picture:', { id, updates, hasProfilePicture: !!profilePicture });
      set({ isLoading: true, error: null });
      
      // Build the request payload as a single JSON object
      const requestData: Record<string, any> = {};
      
      // Add direct user fields
      if ('username' in updates && updates.username) requestData['username'] = updates.username;
      if ('role' in updates && updates.role) requestData['role'] = updates.role;
      if ('status_id' in updates && updates.status_id !== undefined) requestData['status_id'] = updates.status_id;
      
      // Build contact object for nested fields
      const contactData: Record<string, any> = {};
      let hasContactUpdates = false;
      
      if (updates['first_name'] !== undefined) {
        contactData['first_name'] = updates['first_name'];
        hasContactUpdates = true;
      }
      if (updates['last_name'] !== undefined) {
        contactData['last_name'] = updates['last_name'];
        hasContactUpdates = true;
      }
      if ('phone' in updates && updates['phone'] !== undefined) {
        contactData['phone'] = String(updates['phone']);
        hasContactUpdates = true;
      }
      
      // Handle profile picture
      if (profilePicture !== undefined) {
        if (profilePicture === null) {
          // To remove profile picture, set it to null
          contactData['profile_picture'] = null;
          hasContactUpdates = true;
        } else if (typeof profilePicture === 'string' && profilePicture.startsWith('data:image')) {
          contactData['profile_picture'] = profilePicture;
          hasContactUpdates = true;
        } else if (profilePicture instanceof File) {
          // Convert File to base64 string
          try {
            const base64String = await convertFileToBase64(profilePicture);
            contactData['profile_picture'] = base64String;
            hasContactUpdates = true;
          } catch (error) {
            console.error('Error converting File to base64:', error);
            throw new Error('Failed to process profile picture file');
          }
        }
      }
      
      // Add contact object if there are updates
      if (hasContactUpdates) {
        requestData['contact'] = contactData;
      }
      
      // Log the request data for debugging
      console.log('Sending update request with data:', {
        url: USERS.BY_ID(id),
        requestData,
        hasProfilePicture: !!profilePicture,
        profilePictureType: typeof profilePicture,
        profilePicturePreview: typeof profilePicture === 'string' ? profilePicture.substring(0, 50) + '...' : '[File]',
        contactHasProfilePicture: !!requestData['contact']?.['profile_picture'],
        contactProfilePicturePreview: requestData['contact']?.['profile_picture']?.substring?.(0, 100) + '...',
        fullRequestData: JSON.stringify(requestData, null, 2),
        originalUpdates: updates
      });
      
      // Use regular JSON for all updates (including profile pictures as base64 strings)
      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };
      
      const userResponse = await api.put(USERS.BY_ID(id), requestData, config);
      const updatedUser = userResponse.data;
      
      console.log('User update successful:', updatedUser);
      console.log('Updated user profile picture:', {
        hasContact: !!updatedUser.contact,
        hasProfilePicture: !!updatedUser.contact?.profile_picture,
        profilePictureType: typeof updatedUser.contact?.profile_picture,
        profilePicturePreview: updatedUser.contact?.profile_picture?.substring?.(0, 100) + '...'
      });
      
      set(state => ({
        users: state.users.map(user => 
          user.id === id ? updatedUser : user
        ),
        currentUser: state.currentUser?.id === id ? updatedUser : state.currentUser,
        isLoading: false
      }));
      
      return updatedUser;
    } catch (error: any) {
      console.error('API Error:', error);
      console.error('Error updating user:', error);
      set({ error: 'Failed to update user', isLoading: false });
      throw error;
    }
  },

  deleteUser: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      await api.delete(USERS.BY_ID(id));
      set(state => ({
        users: state.users.filter(u => u.id !== id),
        currentUser: state.currentUser?.id === id ? null : state.currentUser,
        isLoading: false
      }));
    } catch (error) {
      set({ error: 'Failed to delete user', isLoading: false });
      throw error;
    }
  },

  setToken: (token: string | null) => {
    tokenStorage.setToken(token);
    set({ token });
  },

  setCurrentUser: (user: User | null) => set({ currentUser: user }),

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post(AUTH.LOGIN, { email, password });
      
      // Extract token and user data from response
      const token = response.data.token;
      const userData = response.data.user;
      
      if (!token) {
        throw new Error('No token received from server');
      }
      
      // Set token in storage and state
      tokenStorage.setToken(token);
      
      // Get contact data from the Contact field
      const contactData = userData.contact || {};
      
      // Log the raw data for debugging
      console.log('Raw user data:', userData);
      console.log('Contact data from response:', contactData);
      
      // Format user data to match our User type
      // The backend should already provide the correct structure
      const user: User = {
        ...userData,
        username: userData.username || email,
        role: userData.role as 'user' | 'admin' | 'manager',
        organization_id: userData.organization_id || userData.OrganizationID,
        status_id: userData.status_id,
        contact: contactData
      };
            
      set({ 
        token, 
        currentUser: user, 
        isLoading: false,
        error: null 
      });
      
      // Set up token refresh
      const refreshTime = 14 * 60 * 1000; // 14 minutes
      if (window.tokenRefreshTimeout) {
        clearTimeout(window.tokenRefreshTimeout);
      }
      window.tokenRefreshTimeout = setTimeout(() => get().refreshToken(), refreshTime);
      
      return user;
    } catch (error) {
      set({ error: 'Login failed. Please check your credentials.', isLoading: false });
      throw error;
    }
  },

  // Same response handling as login(), but hits the no-credentials demo
  // endpoint (only live when the backend has DEMO_LOGIN_ENABLED=true).
  loginDemo: async () => {
    set({ isLoading: true, error: null });
    try {
      // Backend middleware requires a Content-Type even on an empty-body
      // POST, so send `{}` explicitly rather than omitting the body.
      const response = await api.post(AUTH.LOGIN_DEMO, {});

      const token = response.data.token;
      const userData = response.data.user;

      if (!token) {
        throw new Error('No token received from server');
      }

      tokenStorage.setToken(token);
      const contactData = userData.contact || {};

      const user: User = {
        ...userData,
        username: userData.username || userData.email,
        role: userData.role as 'user' | 'admin' | 'manager',
        organization_id: userData.organization_id || userData.OrganizationID,
        status_id: userData.status_id,
        contact: contactData
      };

      set({
        token,
        currentUser: user,
        isLoading: false,
        error: null
      });

      const refreshTime = 14 * 60 * 1000; // 14 minutes
      if (window.tokenRefreshTimeout) {
        clearTimeout(window.tokenRefreshTimeout);
      }
      window.tokenRefreshTimeout = setTimeout(() => get().refreshToken(), refreshTime);

      return user;
    } catch (error) {
      set({ error: 'Demo login failed. The demo may not be enabled on this server.', isLoading: false });
      throw error;
    }
  },

  logout: async () => {
    // Clear token and user data - logout is handled client-side
    tokenStorage.clearToken();
    set({
      currentUser: null,
      token: null,
      error: null,
      isLoading: false
    });
  },

  refreshToken: async () => {
    try {
      console.log('Refreshing token...');
      // Get the current token from our storage system
      const currentToken = tokenStorage.getToken();
      
      // Include both withCredentials for cookies AND the current token in Authorization header
      const response = await api.post(AUTH.REFRESH, {}, { 
        withCredentials: true,
        headers: {
          Authorization: currentToken ? `Bearer ${currentToken}` : ''
        }
      });
      
      const { token } = response.data;
      
      if (token) {
        get().setToken(token);
        console.log('Token refreshed successfully');
        return token;
      }
      
      throw new Error('No token in refresh response');
    } catch (error: any) {
      console.error('Token refresh failed:', error);
      // Don't immediately logout on refresh failure - retry with original token first
      if (error.response?.status === 401) {
        console.log('Refresh token expired or invalid');
        get().logout();
      }
      throw error;
    }
  },

  checkAuth: async (forceRefresh: boolean = false): Promise<boolean> => {
    const token = get().token || tokenStorage.getToken();
    if (!token) {
      set({ currentUser: null, isLoading: false });
      return false;
    }

    // If we already have a current user and not forcing refresh, we're good to go
    if (get().currentUser && !forceRefresh) {
      return true;
    }

    set({ isLoading: true });
    
    try {
      const response = await api.get(USERS.ME);
      const userData = response.data;

      // Get contact data from either Contact (capital C) or contact field
      const contactData = userData.Contact || userData.contact || {};
      
      // Format user data consistently with login
      const user: User = {
        id: userData.id || userData.ID,
        username: userData.username || userData.Username || userData.email || '',
        contact_id: userData.contact_id || userData.ContactID || contactData.id,
        contact: contactData,
        role: (userData.role || userData.Role || 'user') as 'user' | 'admin' | 'manager',
        organization_id: userData.organization_id || userData.OrganizationID,
        status_id: userData.status_id || userData.StatusID,
        last_login: userData.last_login || userData.LastLogin || null,
        created_at: userData.created_at || userData.CreatedAt,
        updated_at: userData.updated_at || userData.UpdatedAt
      };
      
      set({ 
        currentUser: user,
        isLoading: false,
        error: null,
        token: token // Make sure token is set in state
      });
      
      return true;
    } catch (error: any) {
      // If the error is 401, try to refresh the token
      if (error.response?.status === 401) {
        try {
          const newToken = await get().refreshToken();
          if (newToken) {
            // Retry the /me API call with the new token (avoid recursive checkAuth)
            try {
              const retryResponse = await api.get(USERS.ME);
              const retryUserData = retryResponse.data;

              // Get contact data from either Contact (capital C) or contact field
              const retryContactData = retryUserData.Contact || retryUserData.contact || {};
              
              // Format user data consistently
              const retryUser: User = {
                id: retryUserData.id || retryUserData.ID,
                username: retryUserData.username || retryUserData.Username || retryUserData.email || '',
                contact_id: retryUserData.contact_id || retryUserData.ContactID || retryContactData.id,
                contact: retryContactData,
                role: (retryUserData.role || retryUserData.Role || 'user') as 'user' | 'admin' | 'manager',
                organization_id: retryUserData.organization_id || retryUserData.OrganizationID,
                status_id: retryUserData.status_id || retryUserData.StatusID,
                last_login: retryUserData.last_login || retryUserData.LastLogin || null,
                created_at: retryUserData.created_at || retryUserData.CreatedAt,
                updated_at: retryUserData.updated_at || retryUserData.UpdatedAt
              };
              
              set({ 
                currentUser: retryUser,
                isLoading: false,
                error: null,
              });
              
              return true;
            } catch (retryError) {
              // If retry also fails, logout
              await get().logout();
              return false;
            }
          }
          throw new Error('Token refresh did not return a new token');
        } catch (refreshError) {
          await get().logout();
          return false;
        }
      }
      
      // For other errors, log out and clear state
      await get().logout();
      return false;
    }
  },

  getUserById: async (id: number, options: { 
    forceRefresh?: boolean;
    includeContact?: boolean;
  } = {}) => {
    const { 
      forceRefresh = false, 
      includeContact = false 
    } = options;
    
    try {
      // Check if we should use cached data
      const existingUser = get().users.find(u => u.id === id);
      let user = existingUser;
      
      // Fetch from API if forced or user not in cache
      if (forceRefresh || !existingUser) {
        const response = await api.get(`/users/${id}`);
        user = response.data as User;
        
        // Update the store with the fresh data
        set(state => ({
          users: [...state.users.filter(u => u.id !== id), user as User]
        }));
      }

      // Include contact information if requested
      if (includeContact && user?.contact_id) {
        try {
          const contactResponse = await api.get(`/contacts/${user.contact_id}`);
          return { ...user, contact: contactResponse.data };
        } catch (error) {
          console.error('Error fetching contact:', error);
          return user; // Return user without contact if contact fetch fails
        }
      }
      
      return user;
    } catch (error) {
      console.error('Error in getUserById:', error);
      return undefined;
    }
  },
  getUserByEmail: async (email: string, options: { includeContact?: boolean } = {}) => {
    const { includeContact = false } = options;
    
    try {
      // First, search for a contact with this email
      const contactsResponse = await api.get(`/contacts?email=${encodeURIComponent(email)}`);
      const matchingContact = Array.isArray(contactsResponse.data) 
        ? contactsResponse.data[0] 
        : contactsResponse.data;
      
      if (!matchingContact) {
        return undefined;
      }
      
      // If we have a contact, find the associated user
      const users = get().users;
      let user = users.find(u => u.contact_id === matchingContact.id);
      
      // If user not in store, fetch it
      if (!user) {
        const userResponse = await api.get(`/users?contact_id=${matchingContact.id}`);
        const users = Array.isArray(userResponse.data) ? userResponse.data : [userResponse.data];
        if (users.length > 0) {
          user = users[0];
          // Add to store
          set(state => ({
            users: [...state.users.filter(u => u.id !== user?.id), user as User]
          }));
        }
      }
      
      if (!user) {
        return undefined;
      }
      
      // If contact info was requested, include it
      if (includeContact) {
        return { ...user, contact: matchingContact };
      }
      
      return user;
      
    } catch (error) {
      console.error('Error in getUserByEmail:', error);
      return undefined;
    }
  },
  // Password Reset Methods
  requestPasswordReset: async (email: string) => {
    set({ isLoading: true, error: null });
    try {
      await api.post(AUTH.REQUEST_PASSWORD_RESET, { email });
      set({ isLoading: false });
      return true;
    } catch (error) {
      // Don't show an error message here - for security we don't want to reveal
      // whether an email exists in the system or not
      console.error('Error requesting password reset:', error);
      set({ isLoading: false });
      return true; // Always return true for security reasons
    }
  },

  resetPassword: async (token: string, password: string) => {
    set({ isLoading: true, error: null, passwordResetSuccess: false });
    try {
      // Use new_password as that's what the backend expects
      await api.post(AUTH.RESET_PASSWORD, { token, new_password: password });
      set({ isLoading: false, passwordResetSuccess: true });
      return true;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Failed to reset password. Please try again.';
      set({ isLoading: false, error: errorMessage, passwordResetSuccess: false });
      return false;
    }
  },

  setPasswordResetSuccess: (success: boolean) => {
    set({ passwordResetSuccess: success });
  },

  clearError: () => set({ error: null }),
}));


export type { User };
